import * as Yup from 'yup';
import { useState, useRef, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import {
  Link,
  Stack,
  Checkbox,
  TextField,
  IconButton,
  InputAdornment,
  Button,
  FormControlLabel,
  Typography,
  Avatar,
  Chip,
  Card,
  Input,
  Collapse
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/system';
import { makeStyles, styled } from '@mui/styles';
import DriveIcon from '@mui/icons-material/DriveEta';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import secureLocalStorage from 'react-secure-storage';
import { ToastContainer, toast } from 'material-react-toastify';
import 'material-react-toastify/dist/ReactToastify.css';

const useStyles = makeStyles((theme) => ({
  notchedOutline: {
    borderColor: 'red !important'
  },
  headerContainer: {
    position: 'relative',
    height: '100px'
  },
  header: {
    display: 'flex',
    position: 'absolute',
    width: 'calc(100%)',
    top: '-70px',
    alignItems: 'flex-end',
    '& > *': {
      margin: '24px 8px'
    }
  },
  spacer: {
    flexGrow: '1'
  },
  avatar: {
    border: `3px solid white`,
    width: 80,
    height: 80
  },
  actionGroup: {
    display: 'flex',
    width: '330px',
    justifyContent: 'space-between',
    marginRight: 0
  },
  summaryCards: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  summaryCard: {
    margin: 8,
    flexGrow: 1,
    padding: 24
  },
  tripCard: {
    margin: 8,
    padding: 16
  },
  input: {
    display: 'none'
  },
  large: {
    width: 100,
    height: 100
  }
}));

const CssTextField = styled(TextField)({
  '& label.Mui-focused': {
    color: '#f78181'
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: '#f78181'
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'lightgray'
    },
    '&:hover fieldset': {
      borderColor: '#f78181'
    },
    '&.Mui-focused fieldset': {
      borderColor: '#f78181'
    }
  }
});

export default function SetpasswordForm() {
  const [t, i18n] = useTranslation();
  const classes = useStyles();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [notiCompare, setNotiCompare] = useState(false);
  const [notiCurrentPassword, setNotiCurrentPassword] = useState(false);
  const [noti, setNoti] = useState(false);
  const notifyCompare = () => {
    setNotiCompare(true);
    toast.error('You must enter the same password twice in order to confirm it.', {
      position: 'bottom-right',
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true
    });
  };
  const notifySuccess = () => {
    toast.success('Your password has been updated', {
      position: 'bottom-right',
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true
    });
  };
  const notifyCurrentPassword = (messageError) => {
    toast.error(messageError, {
      position: 'bottom-right',
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true
    });
  };

  const PasswordSchema = Yup.object().shape({
    old_password: Yup.string().required('Password is required'),
    new_password: Yup.string().required('Password is required'),
    new2: Yup.string().required('Password is required')
  });
  const userDetails = jwtDecode(localStorage.getItem('access'));
  const formik = useFormik({
    initialValues: {
      new_password: '',
      new2: '',
      old_password: ''
    },
    validationSchema: PasswordSchema,
    onSubmit: (values) => {
      const API_ENDPOINT = process.env.REACT_APP_BASE_URL;
      const token = localStorage.getItem('access');
      if (values.new2 !== values.new_password) {
        notifyCompare();
      } else {
        axios({
          method: 'PUT',
          url: `${API_ENDPOINT}/api/change-password/`,
          headers: { Authorization: `Bearer ${token}` },
          data: values
        })
          .then((res) => {
            notifySuccess();
            setNoti(true);
            setNotiCompare(false);
          })
          .catch((err) => {
            console.log('err', err);
            if (err.response.status >= 500) {
              notifyCurrentPassword('Network Error');
            } else if (err.response.status >= 400 && err.response.status <= 403) {
              notifyCurrentPassword('Enter a valid password and try again.');
            } else {
              notifyCurrentPassword(err.message);
            }
          });
      }
    }
  });

  const { errors, touched, values, isSubmitting, getFieldProps, setFieldValue } = formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
        <ToastContainer />
        <Stack style={{ padding: '30px' }} spacing={3}>
          <CssTextField
            fullWidth
            required
            variant="outlined"
            autoComplete="old_password"
            type={showPassword ? 'text' : 'password'}
            label={t('Motdepasseactuel')}
            {...getFieldProps('old_password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.old_password && errors.old_password)}
            helperText={touched.old_password && errors.old_password}
          />
          <CssTextField
            fullWidth
            required
            autoComplete="new_password"
            type={showPassword ? 'text' : 'password'}
            label={t('Nouveaumotdepasse')}
            {...getFieldProps('new_password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.new_password && errors.new_password)}
            helperText={touched.new_password && errors.new_password}
          />
          <CssTextField
            fullWidth
            required
            autoComplete="new2-password"
            type={showPassword ? 'text' : 'password'}
            label={t('Retapernouveaumotdepasse')}
            {...getFieldProps('new2')}
            InputProps={{
              classes: {
                notchedOutline: notiCompare ? classes.notchedOutline : null
              },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.new2 && errors.new2)}
            helperText={touched.new2 && errors.new2}
          />
          <Box textAlign="right" justifyContent="space-between">
            <Button
              style={{ maxWidth: '200px', backgroundColor: '#f78181' }}
              variant="contained"
              type="submit"
              loading={isSubmitting}
            >
              {t('modifier')}
            </Button>
          </Box>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
